// pages/StatutoryRates.jsx — Admin Board tab
// Phase 10 (July 2026 batch): Statutory Rate Configuration
//
// Canonical, effective-dated, human-confirmed history of the SA corporate
// income tax rate and the SARS IT-180 wear-and-tear rate table. 'Pending'
// rows are proposals — either from the scheduled SARS-check cron, or a
// manual add awaiting a second look — and are NEVER read by any AFS
// generation code until an admin confirms them here. Confirming or
// dismissing is the ONLY way a rate becomes usable by AFS generation.
import { useState, useEffect, Fragment } from 'react';
import {
  getStatutoryRates, confirmStatutoryRate, dismissStatutoryRate, addManualStatutoryRate, editStatutoryRate,
} from '../api/kailux';
import EmptyState from '../components/EmptyState';
import Spinner from '../components/Spinner';
import { CheckCircle2, XCircle, Plus, History, Clock, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import { T } from '../theme/theme'

const RATE_TYPE_LABELS = {
  income_tax: 'SA Corporate Income Tax',
  wear_and_tear_computer: 'IT-180 — Computer Equipment',
  wear_and_tear_motor: 'IT-180 — Motor Vehicles',
  wear_and_tear_furniture: 'IT-180 — Office Furniture',
  wear_and_tear_plant: 'IT-180 — Plant & Machinery',
  wear_and_tear_buildings: 'IT-180 — Buildings',
  wear_and_tear_land: 'IT-180 — Land',
  wear_and_tear_leasehold: 'IT-180 — Leasehold Improvements',
  wear_and_tear_default: 'IT-180 — Default (unclassified assets)',
  pis_audit_threshold_independent: 'Audit — PIS threshold (independent statements)',
  pis_audit_threshold_internal: 'Audit — PIS threshold (internally compiled)',
  fiduciary_assets_audit_threshold: 'Audit — fiduciary assets threshold',
};

// Audit thresholds are plain numbers (points / Rand), not fractions of 1, so
// they are never shown or entered as a percentage.
const NUMBER_UNITS = {
  pis_audit_threshold_independent: 'points',
  pis_audit_threshold_internal: 'points',
  fiduciary_assets_audit_threshold: 'Rand',
};
const isNumberType = (t) => Object.prototype.hasOwnProperty.call(NUMBER_UNITS, t);
const unitOf = (t) => (isNumberType(t) ? NUMBER_UNITS[t] : '%');
// Percent types are entered as a % and stored as a fraction; number types are stored as entered.
const toStored = (t, v) => (isNumberType(t) ? Number(v) : Number(v) / 100);
const toDisplay = (t, v) => (isNumberType(t)
  ? String(Number(v))
  : (Number(v) * 100).toFixed(2).replace(/\.00$/, ''));
function fmtRate(type, rate) {
  if (!isNumberType(type)) return fmtPct(rate);
  const n = Number(rate);
  if (Number.isNaN(n)) return '—';
  return NUMBER_UNITS[type] === 'Rand' ? `R${n.toLocaleString('en-ZA')}` : `${n} points`;
}

function fmtPct(rate) {
  const n = Number(rate);
  if (Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(2).replace(/\.00$/, '')}%`;
}


function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
}

export default function StatutoryRates({ onPendingChanged }) {
  const [view, setView] = useState('pending'); // 'pending' | 'history'
  const [data, setData] = useState({ history: [], pending: [], asset_classes: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ rate_type: 'income_tax', rate: '', effective_from: '', effective_to: '', notes: '' });
  // 22 Jul 2026 follow-up: manual edit of an existing confirmed row (previously
  // only add/confirm/dismiss existed — see kailux-master-plan.md §4 #12).
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ rate: '', effective_from: '', effective_to: '', notes: '' });
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  const load = () => {
    setLoading(true);
    getStatutoryRates()
      .then(setData)
      .catch(() => setData({ history: [], pending: [], asset_classes: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const decide = async (entry, action) => {
    setBusyId(entry.id);
    try {
      if (action === 'confirm') await confirmStatutoryRate(entry.id);
      else await dismissStatutoryRate(entry.id, '');
      setData(d => ({ ...d, pending: d.pending.filter(p => p.id !== entry.id) }));
      if (onPendingChanged) onPendingChanged();
      load();
    } catch (err) {
      alert(err.response?.data?.detail || `Could not ${action} this rate.`);
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      rate: toDisplay(row.rate_type, row.rate),
      effective_from: row.effective_from || '',
      effective_to: row.effective_to || '',
      notes: row.notes || '',
    });
  };

  const submitEdit = async (rowId, rowType) => {
    setBusyId(rowId);
    try {
      const result = await editStatutoryRate(rowId, {
        rate: editForm.rate !== '' ? toStored(rowType, editForm.rate) : undefined,
        effectiveFrom: editForm.effective_from || undefined,
        effectiveTo: editForm.effective_to || null, // allow explicitly clearing to "current"
        notes: editForm.notes,
      });
      if (result.warnings && result.warnings.length > 0) {
        alert(`Saved, but check this:\n\n${result.warnings.join('\n')}`);
      }
      setEditingId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not save this edit.');
    } finally {
      setBusyId(null);
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!addForm.rate || !addForm.effective_from) {
      alert('Rate and effective-from date are required.');
      return;
    }
    try {
      await addManualStatutoryRate(
        addForm.rate_type,
        toStored(addForm.rate_type, addForm.rate), // % types: entered as %, stored as a fraction; thresholds as entered
        addForm.effective_from,
        addForm.effective_to || null,
        addForm.notes,
      );
      setShowAddForm(false);
      setAddForm({ rate_type: 'income_tax', rate: '', effective_from: '', effective_to: '', notes: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not add this rate.');
    }
  };

  const tabBtn = (key, label, Icon, count) => (
    <button onClick={() => setView(key)}
      style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px',
        border:`1px solid ${view===key?T.or:T.bd}`, background: view===key ? T.ord : 'transparent',
        color: view===key ? T.or : T.mt, fontSize:'13px', fontWeight:600, cursor:'pointer' }}>
      <Icon size={14} /> {label}{count > 0 && ` (${count})`}
    </button>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px', flexWrap:'wrap', gap:'12px' }}>
        <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:800, color:T.tx, margin:0 }}>
          Statutory Rates
        </h1>
        <div style={{ display:'flex', gap:'8px' }}>
          {tabBtn('pending', 'Pending Review', Clock, data.pending.length)}
          {tabBtn('history', 'Confirmed History', History, 0)}
          <button onClick={() => setShowAddForm(s => !s)}
            style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px',
              border:'none', background:T.or, color:T.onpri, fontSize:'13px', fontWeight:700, cursor:'pointer' }}>
            <Plus size={14} /> Add rate manually
          </button>
        </div>
      </div>
      <p style={{ fontSize:'13px', color:T.mt, marginBottom:'20px', maxWidth:'720px' }}>
        No SARS API exists, so the scheduled check can only ever <em>propose</em> a change here — it
        never writes to the confirmed table itself. AFS generation only ever reads confirmed rows;
        the preparing accountant still has final say for their own specific engagement.
      </p>

      {showAddForm && (
        <form onSubmit={submitManual} style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
          padding:'18px', marginBottom:'20px', display:'flex', flexDirection:'column', gap:'12px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px' }}>
            <label style={{ fontSize:'12px', color:T.mt }}>
              Rate type
              <select value={addForm.rate_type} onChange={e => setAddForm(f => ({ ...f, rate_type: e.target.value }))}
                style={{ width:'100%', marginTop:'4px', padding:'8px', background:T.s3, border:`1px solid ${T.bd}`,
                  borderRadius:'8px', color:T.tx, fontSize:'13px' }}>
                {Object.entries(RATE_TYPE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </label>
            <label style={{ fontSize:'12px', color:T.mt }}>
              Rate ({unitOf(addForm.rate_type)})
              <input type="number" step="0.01" value={addForm.rate}
                onChange={e => setAddForm(f => ({ ...f, rate: e.target.value }))}
                placeholder="e.g. 27"
                style={{ width:'100%', marginTop:'4px', padding:'8px', background:T.s3, border:`1px solid ${T.bd}`,
                  borderRadius:'8px', color:T.tx, fontSize:'13px', boxSizing:'border-box' }} />
            </label>
            <label style={{ fontSize:'12px', color:T.mt }}>
              Effective from
              <input type="date" value={addForm.effective_from}
                onChange={e => setAddForm(f => ({ ...f, effective_from: e.target.value }))}
                style={{ width:'100%', marginTop:'4px', padding:'8px', background:T.s3, border:`1px solid ${T.bd}`,
                  borderRadius:'8px', color:T.tx, fontSize:'13px', boxSizing:'border-box' }} />
            </label>
            <label style={{ fontSize:'12px', color:T.mt }}>
              Effective to (optional)
              <input type="date" value={addForm.effective_to}
                onChange={e => setAddForm(f => ({ ...f, effective_to: e.target.value }))}
                style={{ width:'100%', marginTop:'4px', padding:'8px', background:T.s3, border:`1px solid ${T.bd}`,
                  borderRadius:'8px', color:T.tx, fontSize:'13px', boxSizing:'border-box' }} />
            </label>
          </div>
          <input placeholder="Notes (e.g. source / reason for this entry)" value={addForm.notes}
            onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
            style={{ padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`, borderRadius:'8px',
              color:T.tx, fontSize:'13px', outline:'none' }} />
          <div style={{ display:'flex', gap:'10px' }}>
            <button type="submit" style={{ padding:'10px 16px', borderRadius:'8px', border:'none',
              background:T.gn, color:T.onpri, fontSize:'13px', fontWeight:700, cursor:'pointer' }}>
              Save confirmed rate
            </button>
            <button type="button" onClick={() => setShowAddForm(false)}
              style={{ padding:'10px 16px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:'transparent', color:T.mt, fontSize:'13px', cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? <Spinner /> : view === 'pending' ? (
        data.pending.length === 0 ? (
          <EmptyState title="Nothing pending" message="No proposed rate changes awaiting review." />
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
            {data.pending.map(entry => (
              <div key={entry.id} style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
                  <div>
                    <div style={{ fontSize:'12px', color:T.mt, marginBottom:'4px' }}>
                      {RATE_TYPE_LABELS[entry.rate_type] || entry.rate_type}
                    </div>
                    <div style={{ fontSize:'18px', fontWeight:700, color:T.am, fontFamily:"'DM Mono',monospace" }}>
                      {fmtRate(entry.rate_type, entry.rate)}
                    </div>
                    <div style={{ fontSize:'12px', color:T.mt }}>
                      Proposed effective {fmtDate(entry.effective_from)} · source: {entry.source}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                    <button onClick={() => decide(entry, 'confirm')} disabled={busyId === entry.id}
                      style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 16px',
                        borderRadius:'8px', border:'none', background:T.gn, color:T.onpri,
                        fontSize:'13px', fontWeight:700, cursor: busyId===entry.id ? 'not-allowed' : 'pointer' }}>
                      <CheckCircle2 size={14} /> Confirm
                    </button>
                    <button onClick={() => decide(entry, 'dismiss')} disabled={busyId === entry.id}
                      style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 16px',
                        borderRadius:'8px', border:'none', background:T.rd, color:T.onpri,
                        fontSize:'13px', fontWeight:700, cursor: busyId===entry.id ? 'not-allowed' : 'pointer' }}>
                      <XCircle size={14} /> Dismiss
                    </button>
                  </div>
                </div>
                {entry.notes && (
                  <div style={{ marginTop:'12px', fontSize:'12px', color:T.lt, fontStyle:'italic' }}>
                    {entry.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        data.history.length === 0 ? (
          <EmptyState title="No history yet" message="No confirmed statutory rates on record." />
        ) : (
          <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
              <thead>
                <tr style={{ background:T.s2, textAlign:'left' }}>
                  {['Rate type', 'Rate', 'Effective from', 'Effective to', 'Source', 'Changed by', 'Actions'].map(h => (
                    <th key={h} style={{ padding:'10px 14px', color:T.mt, fontWeight:600, fontSize:'12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.history.map(row => {
                  const isEditing = editingId === row.id;
                  const editCount = Array.isArray(row.edit_history) ? row.edit_history.length : 0;
                  const isExpanded = expandedHistoryId === row.id;
                  return (
                  <Fragment key={row.id}>
                    <tr style={{ borderTop:`1px solid ${T.bd}` }}>
                      {isEditing ? (
                        <>
                          <td style={{ padding:'10px 14px', color:T.tx }} colSpan={6}>
                            <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center' }}>
                              <span style={{ color:T.mt, fontSize:'12px', minWidth:'160px' }}>
                                {RATE_TYPE_LABELS[row.rate_type] || row.rate_type}
                              </span>
                              <input type="number" step="0.01" value={editForm.rate}
                                onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))}
                                style={{ width:'70px', padding:'6px 8px', background:T.s3, border:`1px solid ${T.bd}`,
                                  borderRadius:'6px', color:T.tx, fontSize:'12px' }} />
                              <span style={{ color:T.mt, fontSize:'12px' }}>{unitOf(row.rate_type)}</span>
                              <input type="date" value={editForm.effective_from}
                                onChange={e => setEditForm(f => ({ ...f, effective_from: e.target.value }))}
                                style={{ padding:'6px 8px', background:T.s3, border:`1px solid ${T.bd}`,
                                  borderRadius:'6px', color:T.tx, fontSize:'12px' }} />
                              <input type="date" value={editForm.effective_to}
                                onChange={e => setEditForm(f => ({ ...f, effective_to: e.target.value }))}
                                placeholder="current" title="Leave blank for 'still in effect'"
                                style={{ padding:'6px 8px', background:T.s3, border:`1px solid ${T.bd}`,
                                  borderRadius:'6px', color:T.tx, fontSize:'12px' }} />
                              <input value={editForm.notes} placeholder="notes"
                                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                                style={{ flex:1, minWidth:'120px', padding:'6px 8px', background:T.s3, border:`1px solid ${T.bd}`,
                                  borderRadius:'6px', color:T.tx, fontSize:'12px' }} />
                            </div>
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            <div style={{ display:'flex', gap:'6px' }}>
                              <button onClick={() => submitEdit(row.id, row.rate_type)} disabled={busyId === row.id}
                                style={{ padding:'6px 10px', borderRadius:'6px', border:'none', background:T.gn,
                                  color:T.onpri, fontSize:'12px', fontWeight:700, cursor: busyId===row.id ? 'not-allowed' : 'pointer' }}>
                                Save
                              </button>
                              <button onClick={() => setEditingId(null)}
                                style={{ padding:'6px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                                  background:'transparent', color:T.mt, fontSize:'12px', cursor:'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding:'10px 14px', color:T.tx }}>{RATE_TYPE_LABELS[row.rate_type] || row.rate_type}</td>
                          <td style={{ padding:'10px 14px', color:T.tx, fontFamily:"'DM Mono',monospace" }}>{fmtRate(row.rate_type, row.rate)}</td>
                          <td style={{ padding:'10px 14px', color:T.lt }}>{fmtDate(row.effective_from)}</td>
                          <td style={{ padding:'10px 14px', color:T.lt }}>{row.effective_to ? fmtDate(row.effective_to) : 'current'}</td>
                          <td style={{ padding:'10px 14px', color:T.mt }}>{row.source}</td>
                          <td style={{ padding:'10px 14px', color:T.mt }}>{row.changed_by || '—'}</td>
                          <td style={{ padding:'10px 14px' }}>
                            <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                              <button onClick={() => startEdit(row)}
                                style={{ display:'flex', alignItems:'center', gap:'4px', padding:'6px 10px',
                                  borderRadius:'6px', border:`1px solid ${T.bd}`, background:'transparent',
                                  color:T.mt, fontSize:'12px', cursor:'pointer' }}>
                                <Pencil size={12} /> Edit
                              </button>
                              {editCount > 0 && (
                                <button onClick={() => setExpandedHistoryId(isExpanded ? null : row.id)}
                                  style={{ display:'flex', alignItems:'center', gap:'4px', padding:'6px 8px',
                                    borderRadius:'6px', border:'none', background:'transparent',
                                    color:T.mt, fontSize:'11px', cursor:'pointer' }}
                                  title="This row has been edited — view history">
                                  edited {editCount}× {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                    {isExpanded && editCount > 0 && (
                      <tr key={`${row.id}-history`} style={{ background:T.s2 }}>
                        <td colSpan={7} style={{ padding:'10px 14px' }}>
                          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                            {row.edit_history.slice().reverse().map((h, i) => (
                              <div key={i} style={{ fontSize:'11px', color:T.lt }}>
                                <span style={{ color:T.mt }}>{fmtDate(h.edited_at)}</span> — {h.edited_by} changed{' '}
                                {Object.entries(h.changed_fields || {}).map(([field, { old, new: newVal }], j) => (
                                  <span key={field}>
                                    {j > 0 && ', '}
                                    <strong>{field}</strong> from "{field === 'rate' ? fmtRate(row.rate_type, old) : (old ?? '—')}" to "{field === 'rate' ? fmtRate(row.rate_type, newVal) : (newVal ?? '—')}"
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
